import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "../../api/client";
import { btnPrimary, type as text, theme } from "../../theme";
import { SheetCard, DimensionHeading, StatusTag } from "../../components/Drawing";
import { LoadingBlock } from "../../components/Spinner";
import { NextSteps } from "../../components/NextSteps";
import { EntityTagsRow } from "../../components/EntityTags";
import { useDeepLinkScroll } from "../../hooks/useDeepLinkScroll";
import { EmptyState, Field, ModalHeader, ModalOverlay, Tri, input, textarea, secondaryBtn, dangerBtn, toDateInput, dateInputToIso, fmtDateOnly } from "../resilience/shared";
import { AttentionList, FrameworkPick, Meta, OwnerField, REVIEW_INTERVALS, attentionFor, msRef, personName, standardsOf, useMembers, useMsEnrolment, useMsPermissions, type Person } from "./shared";
import { attempt, useClearErrorOnDialog, useMsOverview, useRegister } from "./useRegister";

interface Entry {
  id: string; displayId: number | null; kind: "issue" | "interested_party"; frameworkIds: string[]; title: string; category: string;
  description: string | null; requirements: string | null; addressed: boolean | null; response: string | null;
  riskId: string | null; risk: { id: string; displayId: number | null; title: string } | null;
  ownerUserId: string | null; owner: Person | null; ownerName: string | null; status: string;
  reviewIntervalMonths: number; lastReviewedAt: string | null; nextReviewAt: string | null;
}
interface Vocabulary { issueCategories: Array<{ id: string; label: string }>; partyCategories: Array<{ id: string; label: string }> }

type Form = {
  kind: "issue" | "interested_party"; frameworkIds: string[]; title: string; category: string; description: string; requirements: string;
  addressed: boolean | null; response: string; riskId: string; ownerUserId: string; ownerName: string; status: string; reviewIntervalMonths: number; nextReviewAt: string;
};

const blank = (kind: Form["kind"]): Form => ({
  kind, frameworkIds: [], title: "", category: kind === "issue" ? "external" : "customer", description: "", requirements: "", addressed: null,
  response: "", riskId: "", ownerUserId: "", ownerName: "", status: "active", reviewIntervalMonths: 12, nextReviewAt: "",
});

export function ContextRegister() {
  const { data, loading, error, reload } = useRegister<{ entries: Entry[]; vocabulary: Vocabulary }>("/management-system/context");
  const overview = useMsOverview();
  const enrolled = useMsEnrolment();
  const members = useMembers();
  const { canWrite } = useMsPermissions();
  const [params] = useSearchParams();
  const [editing, setEditing] = useState<{ id: string | null; form: Form } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [risks, setRisks] = useState<Array<{ id: string; name: string }> | null>(null);
  useDeepLinkScroll(params.get("entry"), !!data && !overview.loading);
  useClearErrorOnDialog(setFormError, !!editing);
  // The risk register, for linking an issue or a requirement to the risk that answers it.
  useEffect(() => {
    if (!editing || risks) return;
    api.get<{ risks: Array<{ id: string; title: string; displayId: number | null }> }>("/risks")
      .then((d) => setRisks(d.risks.map((r) => ({ id: r.id, name: `${r.displayId != null ? `RISK-${String(r.displayId).padStart(3, "0")} ` : ""}${r.title}` }))))
      .catch(() => setRisks([]));
  }, [editing, risks]);

  if (loading) return <LoadingBlock />;
  if (error || !data) return <p style={{ ...text.body, color: theme.color.dangerText }}>{error}</p>;

  const categoryLabel = (id: string) => [...data.vocabulary.issueCategories, ...data.vocabulary.partyCategories].find((c) => c.id === id)?.label ?? id;
  const issues = data.entries.filter((e) => e.kind === "issue");
  const parties = data.entries.filter((e) => e.kind === "interested_party");

  const open = (e: Entry) => setEditing({
    id: e.id,
    form: {
      kind: e.kind, frameworkIds: e.frameworkIds, title: e.title, category: e.category, description: e.description ?? "", requirements: e.requirements ?? "",
      addressed: e.addressed, response: e.response ?? "", riskId: e.riskId ?? "", ownerUserId: e.ownerUserId ?? "", ownerName: e.ownerName ?? "", status: e.status,
      reviewIntervalMonths: e.reviewIntervalMonths, nextReviewAt: toDateInput(e.nextReviewAt),
    },
  });

  const save = async () => {
    if (!editing) return;
    const f = editing.form;
    const body = {
      ...f, description: f.description || null, requirements: f.requirements || null, response: f.response || null,
      riskId: f.riskId || null, ownerUserId: f.ownerUserId || null, ownerName: f.ownerName || null, nextReviewAt: dateInputToIso(f.nextReviewAt),
    };
    const ok = await attempt(() => (editing.id ? api.put(`/management-system/context/${editing.id}`, body) : api.post("/management-system/context", body)), setFormError, "Could not save the entry");
    if (ok) { setEditing(null); await Promise.all([reload(), overview.reload()]); }
  };

  const markReviewed = async (id: string) => {
    if (await attempt(() => api.post(`/management-system/context/${id}/reviewed`), setFormError, "Could not record the review")) await Promise.all([reload(), overview.reload()]);
  };
  const remove = async (e: Entry) => {
    if (!window.confirm(`Delete ${msRef("context", e.displayId)} ${e.title}?`)) return;
    if (await attempt(() => api.del(`/management-system/context/${e.id}`), setFormError, "Could not delete the entry")) await Promise.all([reload(), overview.reload()]);
  };

  const list = (rows: Entry[], kind: Form["kind"]) => (
    rows.length === 0
      ? <EmptyState
          title={kind === "issue" ? "No issues recorded" : "No interested parties recorded"}
          detail={kind === "issue"
            ? "Record the external and internal issues that affect what the management system must achieve: market, legal, technological, cultural and organisational conditions."
            : "Record who has a stake in the management system — customers, regulators, workers, suppliers — what they require, and which of those requirements you address."}
          action={canWrite ? <button type="button" style={btnPrimary} onClick={() => setEditing({ id: null, form: blank(kind) })}>Add {kind === "issue" ? "an issue" : "an interested party"}</button> : undefined}
        />
      : <div style={{ display: "grid", gap: 8 }}>
          {rows.map((e) => {
            const overdue = e.status === "active" && e.nextReviewAt && new Date(e.nextReviewAt).getTime() + 86_400_000 <= Date.now();
            return (
              <div key={e.id} data-deeplink={e.id} style={{ border: "1px solid var(--c-border)", borderRadius: 3, padding: "10px 12px", background: "var(--c-panel)", opacity: e.status === "archived" ? 0.6 : 1 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span style={{ ...text.ref, color: theme.color.ref }}>{msRef("context", e.displayId)}</span>
                  <span style={text.itemTitle}>{e.title}</span>
                  <span style={text.meta}>{categoryLabel(e.category)}</span>
                  {kind === "interested_party" && (
                    <StatusTag tone={e.addressed === true ? "met" : e.addressed === false ? "none" : "partial"}>
                      {e.addressed === true ? "Addressed" : e.addressed === false ? "Not addressed" : "Not sure"}
                    </StatusTag>
                  )}
                  {overdue && <StatusTag tone="gap">Review overdue</StatusTag>}
                  {canWrite && (
                    <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                      <button type="button" style={secondaryBtn} onClick={() => void markReviewed(e.id)}>Mark reviewed</button>
                      <button type="button" style={secondaryBtn} onClick={() => open(e)}>Edit</button>
                      <button type="button" style={dangerBtn} onClick={() => void remove(e)}>Delete</button>
                    </span>
                  )}
                </div>
                {e.description && <p style={{ ...text.body, margin: "4px 0", whiteSpace: "pre-line" }}>{e.description}</p>}
                {e.requirements && <p style={{ ...text.body, margin: "4px 0", whiteSpace: "pre-line" }}><strong>Requires:</strong> {e.requirements}</p>}
                {e.response && <p style={{ ...text.body, margin: "4px 0", whiteSpace: "pre-line" }}><strong>Response:</strong> {e.response}</p>}
                <Meta>
                  <span>Owner: {personName(e.owner, e.ownerName)}</span>
                  <span>Standards: {standardsOf(e.frameworkIds, enrolled)}</span>
                  <span>Next review: {fmtDateOnly(e.nextReviewAt)}</span>
                  {e.risk && <a href={`/risk-register?risk=${e.risk.id}`} style={{ color: theme.color.ref }}>RISK-{String(e.risk.displayId ?? 0).padStart(3, "0")} {e.risk.title}</a>}
                </Meta>
                <EntityTagsRow entity={{ kind: "ms_context", id: e.id }} readOnly={!canWrite} style={{ marginTop: 6 }} />
              </div>
            );
          })}
        </div>
  );

  const f = editing?.form;
  const set = (patch: Partial<Form>) => editing && setEditing({ ...editing, form: { ...editing.form, ...patch } });
  const categories = f?.kind === "issue" ? data.vocabulary.issueCategories : data.vocabulary.partyCategories;

  return (
    <>
      <AttentionList items={attentionFor(overview.data?.attention ?? [], "ms_context")} />
      {formError && !editing && <p style={{ ...text.body, color: theme.color.dangerText }}>{formError}</p>}
      <SheetCard label="External and internal issues" reference="4.1" actions={canWrite ? <button type="button" style={secondaryBtn} onClick={() => setEditing({ id: null, form: blank("issue") })}><Plus size={12} /> Issue</button> : undefined}>
        {list(issues, "issue")}
      </SheetCard>
      <div style={{ height: 14 }} />
      <SheetCard label="Interested parties and their requirements" reference="4.2" actions={canWrite ? <button type="button" style={secondaryBtn} onClick={() => setEditing({ id: null, form: blank("interested_party") })}><Plus size={12} /> Interested party</button> : undefined}>
        {list(parties, "interested_party")}
      </SheetCard>

      {editing && f && (
        <ModalOverlay onClose={() => setEditing(null)}>
          <ModalHeader title={`${editing.id ? "Edit" : "Add"} ${f.kind === "issue" ? "issue" : "interested party"}`} onClose={() => setEditing(null)} />
          <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* Ticks as the form is filled in: what 4.1 / 4.2 expect of each entry. */}
            <div style={{ gridColumn: "1 / -1" }}>
              <NextSteps items={[
                { done: !!f.title.trim(), label: f.kind === "issue" ? "Name the issue" : "Name the interested party" },
                { done: !!f.description.trim(), label: f.kind === "issue" ? "Say how it affects the management system" : "Say who they are" },
                ...(f.kind === "interested_party" ? [
                  { done: !!f.requirements.trim(), label: "Record their relevant needs and expectations" },
                  { done: f.addressed !== null, label: "Decide whether the management system addresses them" },
                ] : []),
                { done: !!f.response.trim(), label: f.kind === "issue" ? "Say how it is handled" : "Say how the requirements are met and monitored" },
                { done: !!(f.ownerUserId || f.ownerName.trim()), label: "Name an owner" },              ]} />
            </div>
            <Field label={f.kind === "issue" ? "Issue" : "Interested party"} span2>
              <input style={input} maxLength={300} value={f.title} onChange={(e) => set({ title: e.target.value })} />
            </Field>
            <Field label="Category">
              <select style={input} value={f.category} onChange={(e) => set({ category: e.target.value })}>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select style={input} value={f.status} onChange={(e) => set({ status: e.target.value })}>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </Field>
            <Field label={f.kind === "issue" ? "How it affects the management system" : "Who they are"} span2>
              <textarea style={textarea} rows={3} value={f.description} onChange={(e) => set({ description: e.target.value })} />
            </Field>
            {f.kind === "interested_party" && (
              <>
                <Field label="Their relevant needs and expectations" span2>
                  <textarea style={textarea} rows={3} value={f.requirements} onChange={(e) => set({ requirements: e.target.value })} />
                </Field>
                <Field label="Addressed through the management system?" span2 help="Which of these requirements the management system takes on.">
                  <Tri value={f.addressed} onChange={(v) => set({ addressed: v })} />
                </Field>
              </>
            )}
            <Field label={f.kind === "issue" ? "How it is handled" : "How the requirements are met and monitored"} span2>
              <textarea style={textarea} rows={2} value={f.response} onChange={(e) => set({ response: e.target.value })} />
            </Field>
            <Field label="Risk that answers it" span2 help="Optional: the risk register entry that treats this issue or requirement.">
              <select style={input} value={f.riskId} onChange={(e) => set({ riskId: e.target.value })}>
                <option value="">None</option>
                {f.riskId && !(risks ?? []).some((r) => r.id === f.riskId) && <option value={f.riskId}>Current risk</option>}
                {(risks ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </Field>
            <OwnerField userId={f.ownerUserId} name={f.ownerName} members={members} onChange={(o) => set({ ownerUserId: o.userId, ownerName: o.name })} />
            <Field label="Review every">
              <select style={input} value={f.reviewIntervalMonths} onChange={(e) => set({ reviewIntervalMonths: Number(e.target.value) })}>
                {REVIEW_INTERVALS.map((m) => <option key={m} value={m}>{m} months</option>)}
              </select>
            </Field>
            <Field label="Next review" help="Left empty, one interval from today. It appears on the calendar.">
              <input type="date" style={input} value={f.nextReviewAt} onChange={(e) => set({ nextReviewAt: e.target.value })} />
            </Field>
            <FrameworkPick allowed={enrolled} value={f.frameworkIds} onChange={(ids) => set({ frameworkIds: ids })} />
            {formError && <p style={{ gridColumn: "1 / -1", margin: 0, ...text.body, color: theme.color.dangerText }}>{formError}</p>}
            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" style={secondaryBtn} onClick={() => setEditing(null)}>Cancel</button>
              <button type="button" style={btnPrimary} disabled={!f.title.trim()} onClick={() => void save()}>Save</button>
            </div>
          </div>
        </ModalOverlay>
      )}
      <DimensionHeading as="h3" style={{ marginTop: 18 }}>Why this matters</DimensionHeading>
      <p style={text.hint}>The issues and interested parties set the scope of the management system and feed every management review. Linked risks show the risk register answering them; review each entry at its interval and record changes for the next review.</p>
    </>
  );
}
